/* cm-popups.js — CasMarkt v2.0 items 4 and 5.
   Cookie consent card + welcome discount popup. Rendered from
   layout/theme.liquid, styled in cm-popups.css.

   No libraries, no dependencies on other cm-* scripts. Everything degrades:
   with JavaScript off both sections render hidden and the site behaves as it
   did before, and the welcome form still posts because it is a real
   {% form 'customer' %}.

   State (v2.2 item 2 split it in two on purpose):
     localStorage   cm_consent_v1       {"v":1,"analytics":bool,"marketing":bool,"ts":epoch}
     localStorage   cm_welcome_v1       "subscribed" — permanent, no card, no tab
     sessionStorage cm_welcome_session  "card" | "tab" — the offer is spent for
                                        this session; "tab" means the visitor
                                        closed it and the side tab stays put
     sessionStorage cm_welcome_pending  marks which form posted

   Closing is a session decision, not a lifetime one: a visitor who comes back
   tomorrow gets the offer again. Subscribing is the only permanent state.

   The consent choice is handed to Shopify so it actually governs tracking —
   see applyConsent() below. */
(function () {
  'use strict';

  var CONSENT_KEY = 'cm_consent_v1';
  var WELCOME_KEY = 'cm_welcome_v1';
  var WELCOME_SESSION_KEY = 'cm_welcome_session';
  var PENDING_KEY = 'cm_welcome_pending';
  var CONSENT_EVENT = 'cm:consent';

  /* Card → tab has to read as one movement, so it runs well inside the 200ms
     the brief allows: the card's leave transition and the tab's slide are both
     150ms in cm-popups.css and they start on the same frame. */
  var COLLAPSE_MS = 150;

  var designMode = !!(window.Shopify && window.Shopify.designMode);

  /* Timing checks without shipping console noise: add ?cm_debug=1 to any URL
     (or set window.CM_POPUPS_DEBUG) and every step prints its performance.now()
     stamp. Silent for everyone else. */
  var debugOn = /[?&]cm_debug=1/.test(window.location.search) || !!window.CM_POPUPS_DEBUG;

  function nowMs() {
    return window.performance && typeof window.performance.now === 'function'
      ? window.performance.now()
      : Date.now();
  }

  function log(message, extra) {
    if (!debugOn || !window.console || typeof window.console.log !== 'function') return;
    window.console.log('[cm-popups] ' + nowMs().toFixed(1) + 'ms ' + message, extra === undefined ? '' : extra);
  }

  /* --- storage that never throws ----------------------------------------
     Safari in private mode and any browser with site data blocked throw on
     both read and write, and a consent card that crashes there is worse than
     one that simply asks again. */
  function readStore(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeStore(key, value) {
    if (designMode) return;
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* nothing to do: the choice still applies for this page view */
    }
  }

  /* sessionStorage throws in exactly the same places localStorage does. */
  function readSession(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeSession(key, value) {
    if (designMode) return;
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* the page view still behaves correctly, it just cannot remember */
    }
  }

  function clearSession(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (e) {
      /* ignore */
    }
  }

  function readConsent() {
    var raw = readStore(CONSENT_KEY);
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return { analytics: !!parsed.analytics, marketing: !!parsed.marketing };
    } catch (e) {
      return null;
    }
  }

  /* --- hand the choice to Shopify ---------------------------------------
     Shopify.loadFeatures pulls in the consent-tracking API on demand (it is
     not on the page until something asks for it), then
     customerPrivacy.setTrackingConsent records the visitor's decision, which
     is what actually gates Shopify's own analytics and marketing pixels.

     Four buckets, three switches: "preferences" follows the analytics switch
     (remembering what someone looked at is the same bargain), "sale_of_data"
     follows the marketing switch. Essential is not a bucket in this API — it
     is the traffic that runs regardless, which is why its switch is fixed on
     and disabled in the card. */
  function applyConsent(state) {
    var S = window.Shopify;
    if (!S || typeof S.loadFeatures !== 'function') return;

    S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
      if (error) return;
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || typeof cp.setTrackingConsent !== 'function') return;

      cp.setTrackingConsent(
        {
          analytics: !!state.analytics,
          marketing: !!state.marketing,
          preferences: !!state.analytics,
          sale_of_data: !!state.marketing
        },
        function () {
          /* Shopify reports its own result; nothing on the page depends on it. */
        }
      );
    });
  }

  function saveConsent(state) {
    var payload = {
      v: 1,
      analytics: !!state.analytics,
      marketing: !!state.marketing,
      ts: Date.now()
    };
    writeStore(CONSENT_KEY, JSON.stringify(payload));
    applyConsent(payload);
    document.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: payload }));
  }

  function motionOK() {
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Add .is-open on the next frame so the entrance transition has a state to
     move away from; without the double rAF the element is painted already
     open and nothing animates. */
  function reveal(el) {
    el.hidden = false;
    if (!motionOK()) {
      el.classList.add('is-open');
      return;
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        el.classList.add('is-open');
      });
    });
  }

  function conceal(el, done, ms) {
    if (!motionOK()) {
      el.classList.remove('is-open');
      el.hidden = true;
      if (done) done();
      return;
    }
    el.classList.remove('is-open');
    el.classList.add('is-leaving');
    window.setTimeout(
      function () {
        el.classList.remove('is-leaving');
        el.hidden = true;
        if (done) done();
      },
      typeof ms === 'number' ? ms : 320
    );
  }

  /* ======================================================================
     1. Cookie consent
     ====================================================================== */
  function initCookie() {
    var root = document.querySelector('[data-cm-cookie]');
    if (!root) return;

    var card = root.querySelector('.cm-cookie__card');
    var adjust = root.querySelector('[data-cm-consent-toggle]');
    var fields = {
      analytics: root.querySelector('[data-cm-consent-field="analytics"]'),
      marketing: root.querySelector('[data-cm-consent-field="marketing"]')
    };
    var stored = readConsent();
    var openedFromLink = false;

    function fillSwitches(state) {
      if (fields.analytics) fields.analytics.checked = !!state.analytics;
      if (fields.marketing) fields.marketing.checked = !!state.marketing;
    }

    function setPrefs(open) {
      root.classList.toggle('is-prefs', open);
      if (adjust) adjust.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function open(withPrefs) {
      fillSwitches(stored || { analytics: true, marketing: false });
      setPrefs(!!withPrefs);
      reveal(root);
      if (withPrefs && fields.analytics) {
        window.setTimeout(function () {
          fields.analytics.focus();
        }, 380);
      }
    }

    function close() {
      conceal(root, function () {
        setPrefs(false);
      });
    }

    function decide(state) {
      stored = { analytics: !!state.analytics, marketing: !!state.marketing };
      saveConsent(stored);
      close();
    }

    root.addEventListener('click', function (event) {
      var button = event.target.closest('[data-cm-consent]');
      if (!button) return;
      var action = button.getAttribute('data-cm-consent');

      if (action === 'all') decide({ analytics: true, marketing: true });
      else if (action === 'essential') decide({ analytics: false, marketing: false });
      else if (action === 'save') {
        decide({
          analytics: fields.analytics ? fields.analytics.checked : false,
          marketing: fields.marketing ? fields.marketing.checked : false
        });
      }
    });

    if (adjust) {
      adjust.addEventListener('click', function () {
        setPrefs(!root.classList.contains('is-prefs'));
      });
    }

    /* Escape only lets go of a card that was reopened from the footer link.
       On a first visit there is nothing to fall back to yet, so the card
       stays until one of the three buttons is used. */
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || root.hidden) return;
      if (!stored && !openedFromLink) return;
      close();
    });

    /* Two ways in, both ending here: the footer "Cookies" link and the
       "Manage cookie preferences" button on /pages/cookies, which are the same
       href="#cookie-settings". A same-page hash does not always fire
       hashchange — clicking it twice, or landing on the URL with the hash
       already set, fires nothing at all — so the click is handled directly and
       hashchange is only a second net. */
    function openFromLink() {
      openedFromLink = true;
      open(true);
      if (card) card.scrollIntoView({ block: 'nearest' });
      log('cookie prefs opened from link');
    }

    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var link = target.closest('a[href$="#cookie-settings"], .cm-cookie-open, [data-cm-cookie-open]');
      if (!link) return;
      event.preventDefault();
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', '#cookie-settings');
      }
      openFromLink();
    });

    window.addEventListener('hashchange', function () {
      if (window.location.hash === '#cookie-settings') openFromLink();
    });

    if (stored) applyConsent(stored);

    if (window.location.hash === '#cookie-settings') {
      openFromLink();
      return;
    }

    if (designMode) {
      open(false);
      return;
    }

    if (!stored) {
      var delay = parseInt(root.getAttribute('data-cm-delay'), 10);
      if (isNaN(delay)) delay = 1200;
      window.setTimeout(function () {
        if (!readConsent()) open(false);
      }, delay);
    }
  }

  /* ======================================================================
     2. Welcome discount popup
     ====================================================================== */
  function initWelcome() {
    var root = document.querySelector('[data-cm-welcome]');
    if (!root) return;

    var tab = document.querySelector('[data-cm-welcome-tab]');
    var card = root.querySelector('.cm-welcome__card');
    var form = root.querySelector('form');
    var field = root.querySelector('.cm-welcome__field');
    /* Rendered by the section only when Shopify bounced the visitor back from
       a successful subscription — form.posted_successfully? is readable only
       inside the {% form %} block, so the flag is on the markup it produced. */
    var posted =
      !!root.querySelector('[data-cm-posted="true"]') ||
      /[?&]customer_posted=true/.test(window.location.search);
    var subscribed = readStore(WELCOME_KEY) === 'subscribed';
    var session = readSession(WELCOME_SESSION_KEY);
    var lastFocus = null;
    var armed = false;
    var openTimer = null;

    var FOCUSABLE =
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    /* "The tab showed delayed" — it used to wait for the card's 320ms leave to
       finish and then spend two more frames arming its own transition. Now the
       hidden→visible state is flushed with one forced reflow, so the slide
       starts on the very frame the ✕ was pressed. */
    function showTab() {
      if (!tab || subscribed) return;
      tab.hidden = false;
      if (!motionOK()) {
        tab.classList.add('is-open');
        log('tab shown (reduced motion)');
        return;
      }
      void tab.offsetWidth;
      tab.classList.add('is-open');
      log('tab slide started');
    }

    function hideTab() {
      if (!tab) return;
      tab.classList.remove('is-open');
      tab.hidden = true;
    }

    function openCard(why) {
      window.clearTimeout(openTimer);
      lastFocus = document.activeElement;
      hideTab();
      /* Written the moment it opens, not when it closes: leaving the page with
         the card still up has spent this session's showing just the same. */
      if (readSession(WELCOME_SESSION_KEY) !== 'tab') writeSession(WELCOME_SESSION_KEY, 'card');
      reveal(root);
      log('card opened', why || '');
      window.setTimeout(function () {
        if (field) field.focus();
        else if (card) card.focus();
      }, 380);
    }

    /* "If they click X have it move over to the side" — the card does not
       disappear, it becomes the tab. Both halves of the swap start together,
       so what the eye sees is one object moving to the edge rather than a card
       fading out and a tab arriving afterwards. The tab then stays for the
       rest of the session. */
    function dismiss() {
      if (!subscribed) writeSession(WELCOME_SESSION_KEY, 'tab');
      log('dismiss: card leave + tab slide start together');
      /* Tab first, card second, one frame: showTab's reflow flushes the tab's
         closed state, and the card's leave class lands right behind it, so
         both animations are in flight before the frame is painted. Doing it in
         this order also means the tab exists to receive focus when the leave
         finishes — including under reduced motion, where conceal() calls back
         synchronously. */
      showTab();
      conceal(
        root,
        function () {
          /* The ✕ that was just pressed no longer exists on screen, so send
             the keyboard to the thing the card turned into. */
          var back = lastFocus;
          if (!back || !document.contains(back) || root.contains(back)) {
            back = tab && !tab.hidden ? tab : null;
          }
          if (back && typeof back.focus === 'function') back.focus();
          log('dismiss complete');
        },
        COLLAPSE_MS
      );
    }

    root.addEventListener('click', function (event) {
      if (event.target.closest('[data-cm-welcome-close]') || event.target.closest('[data-cm-welcome-scrim]')) {
        event.preventDefault();
        dismiss();
      }
    });

    if (tab) {
      tab.addEventListener('click', function () {
        openCard();
      });
    }

    document.addEventListener('keydown', function (event) {
      if (root.hidden) return;
      if (event.key === 'Escape') {
        dismiss();
        return;
      }
      if (event.key !== 'Tab' || !card) return;

      var items = Array.prototype.filter.call(card.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    /* Marking the submit in sessionStorage is what tells the reloaded page
       that THIS form is the one that posted. Shopify's form.posted_successfully?
       is true for every {% form 'customer' %} on the page after any one of
       them succeeds, so without the marker the footer newsletter would pop
       this card open with a success message it did not earn. */
    if (form) {
      form.addEventListener('submit', function () {
        writeSession(PENDING_KEY, '1');
      });
    }

    /* One timer, no scroll condition. The clock starts when the cookie choice
       is made — or at boot when it was made on an earlier visit — and the card
       opens `data-cm-delay` later. */
    function armOpening(why) {
      if (armed) return;
      armed = true;

      var delay = parseInt(root.getAttribute('data-cm-delay'), 10);
      if (isNaN(delay) || delay < 0) delay = 1500;

      var armedAt = nowMs();
      log('armed by ' + why + ', opening in ' + delay + 'ms');

      openTimer = window.setTimeout(function () {
        if (readStore(WELCOME_KEY) === 'subscribed') return;
        /* Another tab in the same session may have spent the showing while
           this one was counting down. */
        if (readSession(WELCOME_SESSION_KEY)) {
          showTab();
          return;
        }
        log('timer fired ' + (nowMs() - armedAt).toFixed(1) + 'ms after arming');
        openCard('timer');
      }, delay);
    }

    if (designMode) {
      document.addEventListener('shopify:section:select', function (event) {
        if (root.closest('.shopify-section') === event.target) openCard('editor');
      });
      document.addEventListener('shopify:section:deselect', function (event) {
        if (root.closest('.shopify-section') === event.target) dismiss();
      });
      return;
    }

    /* Shopify bounced the visitor back from a successful subscription. That is
       the one permanent state: no card, no tab, ever again. */
    if (posted) {
      var pending = readSession(PENDING_KEY);
      clearSession(PENDING_KEY);
      subscribed = true;
      writeStore(WELCOME_KEY, 'subscribed');
      hideTab();
      log('subscribed');
      if (pending) openCard('subscribed');
      return;
    }

    if (subscribed) return;

    /* The offer is spent for this session: whether it was closed into the tab
       or simply left open on a page the visitor navigated away from, what they
       get now is the tab — immediately, not on a delay. */
    if (session) {
      showTab();
      log('session already used (' + session + '), tab only');
      return;
    }

    /* The cookie card comes first. The discount waits until consent has been
       answered — one card at a time, and the serious one leads. If the cookie
       section is switched off there is no choice to wait for. */
    if (readConsent()) {
      armOpening('stored consent');
    } else if (!document.querySelector('[data-cm-cookie]')) {
      armOpening('no cookie card on the page');
    } else {
      document.addEventListener(
        CONSENT_EVENT,
        function () {
          armOpening('cookie choice');
        },
        { once: true }
      );
    }
  }

  function boot() {
    initCookie();
    initWelcome();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
